import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';

import { convertLangCodeToSingleLetter, pushToGTMDataLayer, handleAnalyticsTrackingCodes } from '../../../utils/helpers';
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import { languageController } from '../../../controllers/language/languageController';
import pidController from '../../../controllers/pidController';
import { useForm } from '../../../hooks/useForm';

import Select from '../../1_Atoms/Forms/Select/Select';
import Checkbox from '../../1_Atoms/Forms/Checkbox/Checkbox';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import Form from '../../2_Molecules/Form/Form';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import FormContainer from '../../2_Molecules/FormContainer/FormContainer';
import FormConfirmation from '../../2_Molecules/FormConfirmation/FormConfirmation';

interface KMIFormExtraParams {
  language: string;
  year?: string;
  model?: string;
  model_list?: string;
  evFlag?: string;
  source?: string;
  formHeading?: string;
  formImage?: string;
  formDisclaimer?: string;
  formSubmitButtonText?: string;
}

interface KMIProps {
  formExtraParams: KMIFormExtraParams;
}

interface KMIFormData {
  [key: string]: string | number | boolean;
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  modelData: string;
  evFlag: boolean;
}

interface ModelOption {
  value: string;
  label: string;
}

interface ModelYearData {
  year?: {
    title: string;
  };
  carline?: {
    voi_leads_name: string;
    title: string;
  };
  show_kmi: boolean;
  show_bnp: boolean;
}

interface ApiResponse {
  Success: boolean;
  state?: string;
  Errors?: string[];
}

interface ModelYearsResponse {
  status: number;
  data?: {
    model_years?: ModelYearData[];
  };
}

const KMI: React.FC<KMIProps> = ({ formExtraParams }) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [formError, setFormError] = useState<string | undefined>(undefined)
  const [name, setfirstName] = useState<string>('');
  //const [optin, setOptin] = useState(false);
  const [isDisabled, setIsDisabled] = useState<string>('disabled')

  const { language, year, model, model_list, evFlag, source, formHeading, formImage, formDisclaimer, formSubmitButtonText } = formExtraParams

  // True if model name and year are provided from parent hidden inputs.
  const hasModelData = (model && year)

  const initState: KMIFormData = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    modelData: hasModelData ? [year, model].join(' ') : '',
    evFlag: false
  }

  const validations: { [key: string]: string[] } = {
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    postalcode: ['required', 'postalcode'],
    modelData: ['required'],
  }

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState,
    validations: validations as any,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const endPointUrl = getEndpointUrl('kmi');
        if (!endPointUrl) {
          setFormError('Configuration error');
          return;
        }
        const modelDataArr = (data.modelData as string).split(" ");
        // Other data to be sent
        const otherData = {
          language: convertLangCodeToSingleLetter(language as any),
          pid: pidController.getPid('kmi'),
          source: source,
          year: modelDataArr[0],
          model: modelDataArr.slice(1).join(' '),
          evFlag: evFlag ? evFlag === 'true' : initState.evFlag
        }

        // Remove whitespace in postalcode
        if (data.postalcode) {
          data.postalcode = (data.postalcode as string).replace(/ /g, "");
        }

        // Remove modelData as no longer needed
        const { modelData, ...restData } = data

        const res = await handleRequest('post', endPointUrl, { ...restData, ...otherData }) as ApiResponse;

        if (res.Success) {
          setfirstName(firstname as string);
          setIsSubmitted(true);
          pushToGTMDataLayer({ formevent: 'submit' });
          handleAnalyticsTrackingCodes({ ...data as any, modelYear: modelDataArr[0], modelCarlineName: modelDataArr.slice(1).join(' ') }, 'Form Submit', 'Keep Me Informed', 'KMI');
          setData(initState);
        } else {
          setFormError(res.state ? res.state : res.Errors?.[0] || 'An error occurred')
        }
      } catch (err) {
        console.log(err)
      }
    },
  })

  useEffect(() => {
    async function fetchMazdaModels() {
      try {
        const endpoint = getEndpointUrl('model-years');
        if (!endpoint) return;
        const res = await handleRequest('get', endpoint) as ModelYearsResponse;

        if (res.status === 200 && res.data) {
          const modelListUnfiltered = res.data['model_years'];
          const modelList = modelListUnfiltered?.filter((item) => item.show_kmi === true);

          if (!modelList) return;
          const mappedList = modelList.map((modelData: ModelYearData) => {
            const optionValue = [modelData.year?.title, modelData.carline?.voi_leads_name].join(' ')
            const optionContent = [modelData.year?.title, modelData.carline?.title].join(' ')
            return ({
              value: optionValue,
              label: optionContent
            })
          })

          setModelOptions(mappedList)
        }
      } catch (err) {
        console.log(err)
      }
    }

    // If "model" data (year + model name) is not provided from the parent inputs
    // get the model list from the api.
    if (!hasModelData) {
      fetchMazdaModels()
    }
  }, [])

  const { firstname, lastname, email, postalcode, modelData } = data;

  let selectedOptions: ModelOption[] | undefined;
  
  if(model_list != undefined){
    const orderedModelValues = model_list.split(',').map((value) => value.trim());
    selectedOptions = orderedModelValues
      .map((value) => modelOptions.find((option) => option.value === value))
      .filter((option): option is ModelOption => Boolean(option));
  }

  return (
    <div
      className="mz-kmi"
      style={{ backgroundImage: formImage ? `url(${formImage})` : '' }}
    >
      <div className="mz-kmi__inner">
        <div className="mz-container">
          <div className="mz-full-width grid-inner-2-10-md grid-inner-4-6-lg">
            <FormContainer
              isSubmitted={isSubmitted}
              confirmation={(
                <FormConfirmation
                  heading={languageController.getCopy('kmi', 'confirmationHeading') || ''}
                  subHeading={languageController.getCopy('kmi', 'confirmationSubHeading')}
                  firstName={name}
                  onClick={() => setIsSubmitted(false)}
                  isDark={true}
                />
              )}
              heading={formHeading ? parse(formHeading) : ''}
            >
              <Form
                type="kmi"
                onSubmit={handleSubmit}
                isDark={true}
                noValidate={true}
                errorMessage={formError}
              >
                {!hasModelData && (
                  <Select
                    name="modelData"
                    label={languageController.getFormCopy('label', 'model')}
                    value={modelData as string}
                    isRequired={true}
                    errorMessage={errors.modelData}
                    onChange={handleChange}
                    placeholder={languageController.getFormCopy('placeholder', 'model')}
                    options={model_list != undefined ? selectedOptions || [] : modelOptions}
                  />
                )}
                <FormGroup md={2}>
                  <Input
                    type="text"
                    name="firstname"
                    label={languageController.getFormCopy('label', 'firstname')}
                    value={firstname as string}
                    isRequired={true}
                    errorMessage={errors.firstname}
                    onChange={handleChange}
                    maxLength={50}
                  />
                  <Input
                    type="text"
                    name="lastname"
                    label={languageController.getFormCopy('label', 'lastname')}
                    value={lastname as string}
                    isRequired={true}
                    errorMessage={errors.lastname}
                    onChange={handleChange}
                    maxLength={50}
                  />
                </FormGroup>
                <Input
                  type="email"
                  name="email"
                  label={languageController.getFormCopy('label', 'email')}
                  value={email as string}
                  isRequired={true}
                  errorMessage={errors.email}
                  onChange={handleChange}
                  maxLength={320}
                />
                <FormGroup md={2}>
                  <Input
                    type="text"
                    name="postalcode"
                    label={languageController.getFormCopy('label', 'postalcode')}
                    value={postalcode as string}
                    isRequired={true}
                    errorMessage={errors.postalcode}
                    onChange={handleChange}
                    maxLength={7}
                  />
                </FormGroup>

                <div className="mz-form-container__disclaimer label label--tight">
                  {formDisclaimer ? parse(formDisclaimer) : ''}
                </div>

                <div className="mz-container-flex">
                    <Button
                      type="submit"
                      className="mz-btn--primary mz-btn--red"
                      label={formSubmitButtonText ? formSubmitButtonText : languageController.getCopy('kmi', 'submit')}
                      // disabled={isDisabled}
                    />
                  </div>
              </Form>
            </FormContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KMI
